import {useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import useBuildOCRDetectedData from '../useBuildOCRDetectedData';
import {PATHS} from 'routes/constants';
import {STORAGE_KEYS} from '../../utils/constants';
import {IV_DOCUMENT_IMAGE_TYPES, SessionStorage} from '@chekin/core';
import {useGetGuestId} from '../useGetGuestId';
import {saveIVScan} from './ivLockStore';
import {useIVStore} from '@chekin/shared';
import {useIVDetectionFlow} from '@chekin/shared/guest-form';
import {useSummary} from 'context/summary';
import {usePath} from '../common';
import {useIVComputedDetails} from '../useIVComputedDetails';
import {useGetNextStepAfterIV} from '../useGetNextStepAfterIV';
import {useVerificationCompleted} from 'components/VerificationCompletedModal';
import {getIVIntentRemoteSession} from './useIVRemoteSync';
import {useLockBodyScroll} from '@chekinapp/ui';

type UseDocumentSelfieDetection = {
  docType?: IV_DOCUMENT_IMAGE_TYPES;
  onlyScanning?: boolean;
  onError?: (error: Error | string) => void;
  livenessVideo?: Blob | null;
  onComplete?: () => void;
};

function useDocumentSelfieDetection({
  onError,
  onlyScanning,
  docType,
  livenessVideo,
  onComplete,
}: UseDocumentSelfieDetection = {}) {
  const {openModal: openIVCompletedModal} = useVerificationCompleted();
  const navigate = useNavigate();
  const isRemoteSession = useIVStore(state => state.isRemoteSession);
  const guestId = useGetGuestId();
  const {biomatch, required} = useIVComputedDetails();
  const {reservationId, countryCode, isSuccess: isSummaryLoaded} = useSummary();
  const {buildFormData} = useBuildOCRDetectedData();
  const {getMainPath} = usePath();
  const {resetScroll} = useLockBodyScroll();
  const {getNextStepDetails} = useGetNextStepAfterIV({docType, onlyScanning});

  const shouldOpenCompletionModal = useCallback(
    (path?: string) => {
      if (!isRemoteSession || !path) {
        return false;
      }

      return (
        path.includes(PATHS.addPersonalDataForm) || path.endsWith(PATHS.guestRegistration)
      );
    },
    [isRemoteSession],
  );

  const flow = useIVDetectionFlow({
    docType,
    onlyScanning,
    livenessVideo,
    biomatch,
    countryCode,
    isCountryLoaded: isSummaryLoaded,
    reservationId,
    getIntentId: getIVIntentRemoteSession,
    getNextStepDetails,
    navigateTo: onComplete ?? navigate,
    onError,
    resetScroll,
    onOCRData: data => {
      const formData = buildFormData(data);
      SessionStorage.set(STORAGE_KEYS.personalFormData, formData);
      if (!onlyScanning) saveIVScan(formData, guestId);
    },
    onAfterSuccessDelay: (nextStep, postHandler) => {
      const shouldShowCompletionModal = shouldOpenCompletionModal(nextStep.path);

      if (shouldShowCompletionModal) {
        openIVCompletedModal(postHandler);
        return true;
      }

      return false;
    },
  });

  const continueRegistration = useCallback(() => {
    if (onComplete) {
      onComplete();
      return;
    }
    navigate(getMainPath(PATHS.addPersonalDataForm));
  }, [onComplete, getMainPath, navigate]);

  return {
    sendSelfieToDetectFace: flow.sendSelfieToDetectFace,
    sendAndReadImageForOCR: flow.sendAndReadImageForOCR,
    sendFrontSideDocument: flow.sendFrontSideDocument,
    sendBackSideDocument: flow.sendBackSideDocument,
    isProcessing: flow.isProcessing,
    processState: flow.processState,
    passedChecks: flow.passedChecks,
    errorMessage: flow.errorMessage,
    errorDescription: flow.errorDescription,
    errorReason: flow.errorReason,
    failureCount: flow.failureCount,
    isServiceFailure: flow.isServiceFailure,
    onTryAgain: flow.onTryAgain,
    onVerifyLater: continueRegistration,
    isRequired: required,
    canVerifyLater: !required || flow.isServiceUnavailable,
    isFinalStep: getNextStepDetails().completed,
    biomatch,
  };
}

export {useDocumentSelfieDetection};
